import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { supabase } from "@/lib/supabase";
import { AuthLayout } from "@/components/Layout";
import { AuthCard } from "@/components/Card";
import { Button } from "@/components/Button";
import { FormField } from "@/components/FormField";
import { Alert } from "@/components/Alert";
import { Logo } from "@/components/Logo";
import { LoadingScreen } from "@/components/LoadingScreen";

export default function AdminMfaSetup() {
  const nav = useNavigate();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  // Enroll a new TOTP factor on mount. If there's already an unverified one
  // hanging around from a half-completed enrollment, clean it up first so
  // Supabase doesn't reject the new enroll for "factor already exists".
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        // Clean up any factor that's not yet 'verified' so the new enroll
        // call doesn't collide. Status type is just 'verified' per the
        // current SDK; treat anything else as in-progress.
        const existing = await supabase.auth.mfa.listFactors();
        const inProgress = existing.data?.totp?.filter((f) => f.status !== "verified") ?? [];
        for (const f of inProgress) {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }
        const { data, error: enrollErr } = await supabase.auth.mfa.enroll({
          factorType: "totp",
          friendlyName: "ForteHealth Admin",
        });
        if (enrollErr) throw enrollErr;
        if (cancelled) return;
        setFactorId(data.id);
        setSecret(data.totp.secret);
        const dataUrl = await QRCode.toDataURL(data.totp.uri, {
          margin: 1,
          width: 224,
          color: { dark: "#1a1a1a", light: "#ffffff" },
        });
        if (cancelled) return;
        setQrDataUrl(dataUrl);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, []);

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!factorId || code.length !== 6) return;
    setVerifying(true);
    setError(null);
    try {
      const challenge = await supabase.auth.mfa.challenge({ factorId });
      if (challenge.error) throw challenge.error;
      const verify = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.data.id,
        code,
      });
      if (verify.error) throw verify.error;
      // Verified — the session is now aal2, RequireAdmin will let them through.
      nav("/admin/users", { replace: true });
    } catch (e) {
      setError((e as Error).message ?? "That code didn't match. Try again.");
    } finally {
      setVerifying(false);
    }
  }

  if (bootstrapping) return <LoadingScreen label="Preparing your authenticator…" />;

  return (
    <AuthLayout>
      <AuthCard>
        <div className="mb-6 flex justify-center"><Logo size="lg" /></div>
        <div className="flex justify-center mb-1.5">
          <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-fh-gray bg-fh-sand rounded-full px-2.5 py-0.5">
            Admin · MFA setup
          </span>
        </div>
        <h1 className="font-serif text-[25px] font-normal text-fh-ink text-center mb-1.5">
          Set up your authenticator
        </h1>
        <p className="text-[13.5px] text-fh-gray text-center mb-5">
          Scan the QR code with Google Authenticator, 1Password, Authy, or
          your password manager's TOTP feature. Then enter the 6-digit code
          it shows.
        </p>

        {error && <div className="mb-4"><Alert tone="error">{error}</Alert></div>}

        {qrDataUrl && (
          <div className="flex justify-center mb-4">
            <img
              src={qrDataUrl}
              alt="MFA QR code"
              className="w-[224px] h-[224px] border border-fh-border rounded-[8px]"
            />
          </div>
        )}

        {secret && (
          <details className="mb-4">
            <summary className="text-[12.5px] text-fh-green cursor-pointer">
              Can't scan? Show the setup key.
            </summary>
            <div className="mt-2 rounded-md bg-fh-cream border border-fh-border px-3 py-2 font-mono text-[12px] text-fh-ink break-all">
              {secret}
            </div>
          </details>
        )}

        <form onSubmit={onVerify} className="flex flex-col gap-3.5">
          <FormField
            label="6-digit code from your authenticator"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <Button
            type="submit"
            loading={verifying}
            disabled={code.length !== 6}
            fullWidth
          >
            Verify and finish
          </Button>
        </form>

        <p className="text-center text-[12px] text-fh-gray mt-5">
          You'll be asked for a code every time you sign in to the admin portal.
        </p>
      </AuthCard>
    </AuthLayout>
  );
}
